/**
 * Clipboard Manager View - Web Interface
 * Full-featured clipboard management for advanced users
 */
import { useEffect, useState } from 'react';
import { useClipboardStore } from '../stores/clipboardStore';
import { useTouchDevice } from '../hooks/useTouchDevice';
import { useSwipeToDelete } from '../hooks/useSwipeToDelete';
import ClipboardItemCard from './ClipboardItemCard';
import FileExportModal from './FileExportModal';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { convertMarkdownToHtml } from '../utils/markdownToHtml';

export default function ClipboardManager() {
  const { items, loadItems, isLoading, deleteItem } = useClipboardStore();
  const isTouch = useTouchDevice();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  const [swipingItemId, setSwipingItemId] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportItem, setExportItem] = useState<any | null>(null);

  // Load items when component mounts
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Close modal on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreviewItem(null);
      }
    };

    if (previewItem) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [previewItem]);

  const handleCopyToClipboard = async (item: any) => {
    try {
      // Get markdown or plain text content
      const markdownContent = item.content_variants?.markdown || item.content_variants?.plain || '';

      // Convert markdown to HTML using a temporary div
      const tempDiv = document.createElement('div');
      tempDiv.className = 'markdown-content';

      // Use a simple markdown to HTML converter
      // For better formatting, we'll use the browser's clipboard API with both HTML and text
      const htmlContent = await convertMarkdownToHtml(markdownContent);

      // Copy both HTML (for rich paste) and plain text (fallback)
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([markdownContent], { type: 'text/plain' })
      });

      await navigator.clipboard.write([clipboardItem]);
      toast.success('Copied to clipboard with formatting!');
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback to plain text if rich copy fails
      try {
        const plainContent = item.content_variants?.plain || '';
        await navigator.clipboard.writeText(plainContent);
        toast.success('Copied to clipboard!');
      } catch (fallbackError) {
        toast.error('Failed to copy to clipboard');
      }
    }
  };

  const handleDeleteItem = async (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation(); // Prevent any parent click handlers

    if (!confirm('Delete this clipboard item?')) return;

    setDeletingId(itemId);
    try {
      await deleteItem(itemId);
      // toast is already shown by the store
    } catch (error) {
      console.error('Failed to delete clipboard item:', error);
      // error toast is already shown by the store
    } finally {
      setDeletingId(null);
    }
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getPlainContent = (item: any): string => {
    return item.content_variants?.plain || '';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Clipboard Items List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-accent-blue border-t-transparent rounded-full animate-spin"></div>
              <div className="text-dark-text text-lg">Loading clipboard items...</div>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-20 h-20 text-dark-text-muted mb-4 opacity-40 mx-auto">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
              <p className="text-lg font-semibold text-dark-text mb-2">
                No clipboard items yet
              </p>
              <p className="text-dark-text-muted max-w-md">
                Items sent to clipboard from conversations will appear here
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                onClick={() => setPreviewItem(item)}
                className="group relative bg-dark-card border border-dark-border rounded-lg p-4 hover:border-accent-blue/50 transition-colors cursor-pointer"
              >
                {/* Delete button - appears on hover */}
                <button
                  onClick={(e) => handleDeleteItem(e, item.id)}
                  disabled={deletingId === item.id}
                  className={`absolute top-2 right-2 p-1.5 bg-dark-bg/80 hover:bg-red-500/20 rounded-md border border-dark-border hover:border-red-500/50 transition-all disabled:opacity-50 z-10 ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Delete clipboard item"
                >
                  {deletingId === item.id ? (
                    <svg className="w-3.5 h-3.5 text-dark-text-muted animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-red-400">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  )}
                </button>

                {/* Card Content */}
                <div className="mb-3">
                  {/* Title and Source Badge */}
                  <div className="flex items-start gap-2 mb-2">
                    <div className="px-2 py-1 bg-accent-blue/20 border border-accent-blue/30 rounded text-xs font-bold text-accent-blue uppercase flex-shrink-0">
                      {item.source}
                    </div>
                    {item.title && (
                      <h3 className="text-sm font-semibold text-dark-text truncate flex-1">
                        {item.title}
                      </h3>
                    )}
                  </div>

                  {/* Content Preview */}
                  <div className="text-sm text-dark-text line-clamp-3 mb-2 bg-dark-bg p-2 rounded font-mono">
                    {truncateText(getPlainContent(item), 150)}
                  </div>

                  {/* Metadata */}
                  <div className="flex flex-col gap-1 text-xs text-dark-text-muted">
                    <span>{formatDate(item.created_at)}</span>
                    <span>{item.character_count || 0} chars • {item.word_count || 0} words</span>
                  </div>

                  {/* Tags */}
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.tags.slice(0, 3).map((tag: string, idx: number) => (
                        <span key={idx} className="px-2 py-1 bg-dark-bg border border-dark-border rounded text-xs text-dark-text-muted">
                          #{tag}
                        </span>
                      ))}
                      {item.tags.length > 3 && (
                        <span className="px-2 py-1 text-xs text-dark-text-muted">
                          +{item.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyToClipboard(item);
                    }}
                    className="flex-1 px-3 py-2 bg-accent-blue text-white text-sm rounded hover:bg-accent-blue/80 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                    </svg>
                    Send to Clipboard
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExportItem(item);
                      setShowExportModal(true);
                    }}
                    className="flex-1 px-3 py-2 bg-accent-orange text-white text-sm rounded hover:bg-accent-orange/80 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Send to File
                  </button>
                  {item.content_variants && Object.keys(item.content_variants).length > 1 && (
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-2 bg-dark-bg border border-dark-border hover:border-accent-blue text-dark-text text-sm rounded transition-colors flex items-center justify-center"
                      title={`${Object.keys(item.content_variants).length} formats`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clipboard Item Preview Modal */}
      {previewItem && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-lg max-w-5xl w-full max-h-[90vh] sm:max-h-[80vh] flex flex-col text-sm sm:text-base"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between p-3 sm:p-4 lg:p-6 border-b border-dark-border">
              <div className="flex-1 min-w-0 pr-2">
                <h2 className="text-lg sm:text-xl font-semibold text-dark-text mb-2 truncate">
                  {previewItem.title || 'Clipboard Item'}
                </h2>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-dark-muted">
                  <span className="px-2 py-1 bg-accent-blue/20 border border-accent-blue/30 rounded text-xs font-bold text-accent-blue uppercase w-fit">
                    {previewItem.source}
                  </span>
                  <span>{formatDate(previewItem.created_at)}</span>
                  <span>{previewItem.character_count || 0} chars • {previewItem.word_count || 0} words</span>
                </div>
              </div>
              <button
                onClick={() => setPreviewItem(null)}
                className="text-dark-muted hover:text-dark-text active:text-dark-text transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -m-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
              {/* Check for image content */}
              {previewItem.content_variants?.image && (
                <div className="mb-4">
                  <img
                    src={previewItem.content_variants.image}
                    alt={previewItem.title || 'Clipboard image'}
                    className="max-w-full h-auto rounded-lg border border-dark-border"
                  />
                </div>
              )}

              {/* Display HTML content if available */}
              {previewItem.content_variants?.html && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">HTML Content</div>
                  <div
                    className="bg-dark-bg border border-dark-border rounded-lg p-4 prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewItem.content_variants.html) }}
                  />
                </div>
              )}

              {/* Display markdown content with styling */}
              {(previewItem.content_variants?.markdown || previewItem.content_variants?.plain) && !previewItem.content_variants?.html && (
                <div className="mb-4">
                  <div className="bg-dark-bg border border-dark-border rounded-lg p-4 max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({ node, children, ...props }: any) => (
                          <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border border-dark-border" {...props}>{children}</table>
                          </div>
                        ),
                        thead: ({ node, ...props }: any) => <thead className="bg-dark-bg" {...props} />,
                        th: ({ node, ...props }: any) => <th className="border border-dark-border px-4 py-2 text-left font-semibold text-dark-text" {...props} />,
                        td: ({ node, ...props }: any) => <td className="border border-dark-border px-4 py-2 text-dark-text" {...props} />,
                        tbody: ({ node, ...props }: any) => <tbody {...props} />,
                        tr: ({ node, ...props }: any) => <tr className="hover:bg-dark-card/50" {...props} />,
                        h1: ({ node, ...props }: any) => <h1 className="text-2xl font-bold mb-4 mt-6 text-dark-text" {...props} />,
                        h2: ({ node, ...props }: any) => <h2 className="text-xl font-bold mb-3 mt-5 text-dark-text" {...props} />,
                        h3: ({ node, ...props }: any) => <h3 className="text-lg font-bold mb-2 mt-4 text-dark-text" {...props} />,
                        p: ({ node, ...props }: any) => <p className="mb-3 text-dark-text" {...props} />,
                        strong: ({ node, ...props }: any) => <strong className="text-dark-text font-bold" {...props} />,
                        li: ({ node, ...props }: any) => <li className="text-dark-text ml-4" {...props} />,
                        ul: ({ node, ...props }: any) => <ul className="list-disc list-inside mb-3" {...props} />,
                        ol: ({ node, ...props }: any) => <ol className="list-decimal list-inside mb-3" {...props} />,
                        blockquote: ({ node, ...props }: any) => <blockquote className="border-l-4 border-accent-blue pl-4 italic my-4 text-dark-text-muted" {...props} />,
                        code: ({ node, inline, ...props }: any) => inline ? (
                          <code className="bg-dark-bg px-1.5 py-0.5 rounded text-sm font-mono text-accent-blue" {...props} />
                        ) : (
                          <code className="block bg-dark-bg rounded-lg p-4 overflow-x-auto border border-dark-border text-xs sm:text-sm" {...props} />
                        ),
                      }}
                    >
                      {previewItem.content_variants.markdown || previewItem.content_variants.plain}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {/* If no content variants, show title or fallback */}
              {!previewItem.content_variants?.plain && !previewItem.content_variants?.html && !previewItem.content_variants?.image && (
                <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                  <p className="text-dark-muted text-sm italic">
                    {previewItem.title || 'No content available'}
                  </p>
                </div>
              )}

              {/* Display all available content variants */}
              {previewItem.content_variants && Object.keys(previewItem.content_variants).length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">
                    Available Formats: {Object.keys(previewItem.content_variants).join(', ')}
                  </div>
                </div>
              )}

              {/* Tags */}
              {previewItem.tags && previewItem.tags.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {previewItem.tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-accent-blue/10 text-accent-blue text-xs rounded"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 lg:p-6 border-t border-dark-border">
              <button
                onClick={() => setPreviewItem(null)}
                className="sm:hidden px-4 py-3 bg-dark-border text-dark-text rounded min-h-[44px] hover:bg-dark-bg active:bg-dark-bg transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDeleteItem(new MouseEvent('click') as any, previewItem.id);
                  setPreviewItem(null);
                }}
                className="px-4 py-3 sm:py-2 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 active:bg-red-500/30 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
                <span className="text-sm sm:text-base">Delete</span>
              </button>
              <button
                onClick={() => {
                  setExportItem(previewItem);
                  setShowExportModal(true);
                }}
                className="px-4 py-3 sm:py-2 bg-accent-orange text-white rounded hover:bg-accent-orange/80 active:bg-accent-orange/70 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                <span className="text-sm sm:text-base">Send to File</span>
              </button>
              <button
                onClick={() => {
                  handleCopyToClipboard(previewItem);
                }}
                className="px-4 py-3 sm:py-2 bg-accent-blue text-white rounded hover:bg-accent-blue/80 active:bg-accent-blue/70 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                </svg>
                <span className="text-sm sm:text-base">Send to Clipboard</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Export Modal */}
      <FileExportModal
        isOpen={showExportModal}
        content={exportItem?.content_variants?.markdown || exportItem?.content_variants?.plain || ''}
        defaultTitle={exportItem?.title}
        onClose={() => {
          setShowExportModal(false);
          setExportItem(null);
        }}
      />
    </div>
  );
}
